import { ProductTableRow } from '../products-data-table/product-data-table-row.model';
import { SelectedPermissionVM } from '../../users-module/permission-groups-data-table/permission-group.model';

export class Attribute {
    public id: number;
    public systemId: string;
    public externalId: string;
    public previousType: string;
    public type: string;
    public name: string;
    public sensitiveData: boolean;
    public containsPersonallyIdentifiableInformation: boolean;
    public container: Container;
    public autocomplete: Autocomplete;
    public measurement: Measurement;
    public values: Array<AttributeValue>;
    public isDynamic: boolean;

    constructor() {
        this.id = 0;
        this.name = '';
        this.container = new Container();
        this.autocomplete = new Autocomplete();
        this.measurement = new Measurement();
        this.values = new Array<AttributeValue>();
    }
}

export class Autocomplete {
    public tableId?: number;
    public preselectionFilter?: string;
    public showOnlyDistinctValues: boolean;
    public isAutocompleteUsedAsProductAttribute: boolean;
    public tableColumns: Array<AutocompleteColumn>;
    public searchableColumns: Array<AutocompleteColumn>;
    public displayedColumns: Array<AutocompleteColumn>;
    public searchMethod: number;

    constructor() {
        this.tableColumns = new Array<AutocompleteColumn>();
        this.searchableColumns = new Array<AutocompleteColumn>();
        this.displayedColumns = new Array<AutocompleteColumn>();
    }
}

export class Measurement {
    public measurementId?: number;
}

export class Container {
    public presentResultsOfProductSearch: boolean;
    public maxContainerRows: string;
    public minContainerRows: string;
    public enablePaging: boolean;
    public defaultNumberOfRows: number;
    public containerUsage: ContainerUsageOptions;
    public actions: ContainerActions;
    public products: Array<ContainerProduct>;
    public properties: Array<ContainerProperty>;
    public validations: Array<ContainerValidationRule>;
    public columns: Array<ContainerColumn>;

    constructor() {
        this.containerUsage = ContainerUsageOptions.Products;
        this.actions = new ContainerActions();
        this.products = new Array<ContainerProduct>();
        this.properties = new Array<ContainerProperty>();
        this.validations = new Array<ContainerValidationRule>();
        this.columns = new Array<ContainerColumn>();
    }
}

export class ContainerActions {
    public openNewConfigurator: boolean;
    public addNewRow: boolean;
    public copyRow: boolean;
    public allowMultipleCopy: boolean;
    public deleteRow: boolean;
    public editRow: boolean;
    public reorderRows: boolean;

    constructor() {
        this.openNewConfigurator = true;
        this.addNewRow = true;
        this.copyRow = true;
        this.deleteRow = true;
        this.reorderRows = true;
        this.editRow = true;
        this.allowMultipleCopy = false;
    }
}

export class ContainerColumn {
    public id: number;
    public name: string;
    public headerLabel: string;
    public footerLabel: string;
    public height?: number;
    public width?: number;
    public total: TotalTypeEnum;
    public formula: string;
    public displayRank: number;
    public calculationRank: number;
    public dataType: DataTypeEnum;
    public decimalPlaces: number;
    public columnType: ColumnTypeEnum;
    public displayType: DisplayTypeEnum;
    public selectedAttributeId: number;
    public selectedAttributeValues: Array<ContainerAttributeValue>;
    public isSelectedAttributeAutocomplete: boolean;
    public isSelectedAttributeQuoteTableAutocomplete: boolean;
    public calculationFormula: string;
    public alwaysIncludeInKOContext: boolean;
    public containsPersonallyIdentifiableInformation: boolean;
    public sensitiveData: boolean;
    public permissions: Array<ContainerPermission>;
    public selectedPermissions: SelectedPermissionCollections;
    public defaultUserTypePermission: ContainerPermissionEnum;
    public tempId: number;

    constructor() {
        this.decimalPlaces = 0;
        this.displayRank = 0;
        this.containsPersonallyIdentifiableInformation = false;
        this.displayType = DisplayTypeEnum.Label;
        this.total = TotalTypeEnum.None;
        this.dataType = DataTypeEnum.String;
        this.columnType = ColumnTypeEnum.StandAloneColumn;
        this.selectedPermissions = new SelectedPermissionCollections();
        this.selectedAttributeValues = new Array<ContainerAttributeValue>();
        this.calculationRank = 0;
    }
}
export class ContainerPermission {
    public columnId: number;
    public userTypeId?: number;
    public value: ContainerPermissionEnum;
}

export class SelectedPermissionCollections {
    public editable = new SelectedPermissionVM();
    public readOnly = new SelectedPermissionVM();
    public required = new SelectedPermissionVM();
    public hidden = new SelectedPermissionVM();
}
export class ContainerAttributeValue {
    public id: number;
    public value: string;
    public valueCode: string;
}
export class ContainerValidationRule {
    public id: number;
    /** UI only because we are adding new properties without going to backend */
    public tempId: number;
    public message: string;
    public name: string;
    public validationFormula: string;
    /** UI only because we are adding new properties without going to backend */
    public attributeId: number;
}
export class ContainerProperty {
    public id: number;
    public name: string;
    /** UI only because we are adding new properties without going to backend */
    public tempId: number;
    public formula: string;
    public calculationRank: number;
    public dataType: DataTypeEnum;
    public type: string;
    public decimalPlaces: number;
    /** rank is id in combination with attributeId */
    public rank: number;
    public includeInJSON: boolean;
    /** UI only because we are adding new properties without going to backend */
    public attributeId: number;

    constructor() {
        this.calculationRank = 0;
        this.rank = 0;
        this.decimalPlaces = 0;
        this.dataType = DataTypeEnum.String;
        this.includeInJSON = false;
    }
}
export class ContainerProduct {
    public id: number;
    public aliasSourceProductId?: number;
    public name: string;
    public version: string;
    public systemId: string;
    public maxRows: string;
    public minRows: string;
    public isActive: boolean;
    public isSimple: boolean;
    public productTypeName: string;
}


export class AutocompleteColumn {
    public tableColumnId: number;
    public columnName: string;
    public displayLabel: string;
    public rank: number;
}

export class AutocompleteColumnRow {
    public id: number;
    public columnName: string;
    public display: boolean;
    public displayLabel: string;
    public isSearchable: boolean;
    public useColumnValueCode: boolean;
    public searchableRank: number;
    public displayRank: number;
    public displayIsDisabled?: boolean;
    public isSearchableIsDisabled?: boolean;
}

export enum DataTypeEnum {
    String = 0,
    Number = 1,
    Currency = 2,
    Date = 3
}

export enum DisplayTypeEnum {
    Label = 0,
    TextBox = 1,
    CheckBox = 2,
    DropDown = 3,
    SelectList = 4,
    RadioButton = 5,
    Multiline = 7,
    SelectorRadioButton = 9,
    SelectorCheckBox = 10,
    AutocompleteCustomTable = 11,
    AutocompleteQuoteTable = 12,
}

export enum TotalTypeEnum {
    None = 0,
    Sum = 1,
    Average = 2,
    Formula = 3
}

export enum ContainerPermissionEnum {
    Hidden = 'Hidden',
    ReadOnly = 'ReadOnly',
    Editable = 'Editable',
    Required = 'Required'
}

export enum ColumnTypeEnum {
    StandAloneColumn = 0,
    ProductPartNumber = 1,
    ProductAttribute = 2
}

export enum ContainerUsageOptions {
    NoProducts = 1,
    Products = 2,
    ProductPartNumber = 3
}

export class AttributeValue {
    public id: number;
    public systemId: string;
    public value: string;
    public rank: number;
    public valueCode: string;
    public productId: number;
    public refProductDisplayName: string;
    public description: string;
    public billingPeriod?: AttributeValueBillingPeriod;
    public unit?: AttributeValueUnit;
    public tempId: number; // Only in UI

    constructor(id: number = 0, systemId: string = '', display: string = '', rank: number = 0, value: string = '', productId: number = 0, refProductDisplayName?: string, description?: string, unit?: AttributeValueUnit, billingPeriod?: AttributeValueBillingPeriod) {
        this.id = id;
        this.tempId = id;
        this.systemId = systemId;
        this.value = display;
        this.rank = rank;
        this.valueCode = value;
        this.productId = productId;
        this.refProductDisplayName = refProductDisplayName;
        this.description = description;
        this.billingPeriod = billingPeriod;
        this.unit = unit;
    }

    loadFromProduct(product: ProductTableRow, attributeName?: string){
        this.systemId = `${product.partNumber}_${attributeName}_cpq`;
        this.value = product.name;
        this.valueCode = product.partNumber;
        this.productId = product.id;
        this.refProductDisplayName = product.name;
    }
}

export enum AttributeValueBillingPeriod {
    CalendarMonthly = 1,
    CalendarQuarterly = 2,
    CalendarHalfYearly = 3,
    CalendarYearly = 4,
    RollingCycleMonthly = 5,
    RollingCycleQuarterly = 6,
    RollingCycleHalfYearly = 7,
    RollingCycleYearly = 8
}

export enum AttributeValueUnit {
    Day = 1,
    Month = 2,
    Year = 3
}


export enum SearchMethodType {
  Contains = 0,
  StartsWith = 1
}
